import { Guid } from '@approvalmax/types';
import { Popup } from '@approvalmax/ui';
import { useAtomValue } from 'jotai';
import { selectors, ui } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useProgressLoading } from 'modules/utils';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { notificationService } from 'services/notification';
import { useChangeWatchers } from 'shared/data/webApp/v1';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { cancelActivePopup, reloadRequest } from '../../actions';
import { getMemberOrUser } from '../../selectors/userSelectors';
import WatcherItem from './components/WatcherItem/WatcherItem';
import { useGetRequest } from './EditWatchersPopup.hooks';
import { messages } from './EditWatchersPopup.messages';
import { Content, Description, WatcherList } from './EditWatchersPopup.styles';

const qa = bemFactory.qa('reql-edit-watchers-popup');

const EditWatchersPopup: FC = memo(() => {
    const dispatch = useDispatch();

    const request = useGetRequest();
    const team = useSelector((state) => (request ? selectors.company.getCompanyTeam(state, request.company) : []));
    const me = useSelector((state) => team.find((user) => user.id === selectors.profile.getProfileUser(state)?.id));
    const allUsers = useSelector(selectors.user.getUsers);

    const contentSplitView = useAtomValue(contentSplitViewState);
    const isBillInSplitView =
        request &&
        contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment &&
        contentSplitView.billId === request.id;

    const readOnly = !request || isBillInSplitView;

    const { mutateAsync: changeWatchers, isLoading: isLoadingChangeWatchers } = useChangeWatchers();

    const [watchers, setWatchers] = useState(
        (request ? request.watchers : [])
            .map((requestWatcher) => requestWatcher.watcher)
            .filter((watcher) => allUsers.find((user) => user.id === watcher.id))
    );

    useProgressLoading(isLoadingChangeWatchers);

    const getUser = useCallback((userId: Guid) => getMemberOrUser(userId, team, allUsers), [allUsers, team]);

    const onAddWatcher = useCallback((user: selectors.types.ExpandedCompanyUser) => {
        setWatchers((prevWatchers) => [...prevWatchers, user]);
    }, []);

    const onRemoveWatcher = useCallback((userId: string) => {
        setWatchers((prevWatchers) => prevWatchers.filter((prevWatcher) => prevWatcher.id !== userId));
    }, []);

    const onDone = useCallback(async () => {
        if (readOnly) {
            dispatch(cancelActivePopup());
        }

        const newWatcherIds = watchers.map((watcher) => watcher.databaseId || '').filter((id) => id);
        const oldWatcherIds = (request ? request.watchers : [])
            .map((requestWatcher) => requestWatcher.watcher.databaseId || '')
            .filter((id) => id);

        const watchersToAdd = newWatcherIds.filter((newWatcherId) => !oldWatcherIds.includes(newWatcherId));
        const watchersToRemove = oldWatcherIds.filter((oldWatcherId) => !newWatcherIds.includes(oldWatcherId));

        if (request && (watchersToAdd.length || watchersToRemove.length)) {
            await changeWatchers(
                {
                    body: {
                        requestId: request.id,
                        companyId: request.companyId,
                        watchersToAdd,
                        watchersToRemove,
                        isAddedDueToMention: false,
                    },
                },
                {
                    onSuccess: () => {
                        notificationService.showInfoToast(messages.successText);
                        dispatch(cancelActivePopup());
                        dispatch(reloadRequest(request.id, request.companyId));
                    },
                }
            );
        } else {
            dispatch(cancelActivePopup());
        }
    }, [changeWatchers, dispatch, readOnly, request, watchers]);

    const possibleWatchers = useMemo(() => {
        if (!request || !me) {
            return [];
        }

        const watcherIds = watchers.map((watcher) => watcher.databaseId || '').filter((id) => id);

        const participantEmails = new Set<Guid>();

        request.steps.forEach((step) => {
            step.participants.forEach((participant) => participantEmails.add(participant.userId));
        });
        request.reviewStep?.reviewers.forEach((reviewer) => {
            participantEmails.add(reviewer.userEmail);
        });

        return team.filter(
            (user) =>
                !watcherIds.includes(user.databaseId || '') &&
                user.id !== request.authorId &&
                !participantEmails.has(user.id || '') &&
                user.status === domain.CompanyUserStatus.Active
        );
    }, [me, request, team, watchers]);

    const watchersMap = useMemo(() => {
        if (!request) return [];

        return watchers.map((watcher) => {
            const expandedUser = getUser(watcher.id);

            const meIsManager = me?.role === domain.CompanyUserRole.Manager;
            const meIsWatcher = watcher.id === me?.id;
            const isJustAddedNotSaved = !request.watchers.find(
                (requestWatcher) => requestWatcher.watcher.id === watcher.id
            );

            const requestWatcher = request.watchers.find((requestWatcher) => requestWatcher.watcher.id === watcher.id);
            const isAddedByMe = requestWatcher ? requestWatcher.addedBy.id === me?.id : false;

            const isAllowedToRemove = !readOnly && (meIsManager || meIsWatcher || isJustAddedNotSaved || isAddedByMe);

            return (
                <WatcherItem
                    key={watcher.id}
                    watcherId={watcher.id}
                    readonly={!isAllowedToRemove}
                    expandedUser={expandedUser}
                    onRemoveWatcher={onRemoveWatcher}
                />
            );
        });
    }, [getUser, me?.id, me?.role, onRemoveWatcher, readOnly, request, watchers]);

    return (
        <Popup.DefaultContent
            className='fs-mask'
            qa={qa()}
            title={readOnly ? messages.readOnlyPopupTitle : messages.popupTitle}
            buttons={
                <Popup.DefaultContent.Button
                    qa={qa('done-button')}
                    disabled={isLoadingChangeWatchers}
                    execute={onDone}
                    title={messages.doneButtonTitle}
                >
                    {messages.doneButtonText}
                </Popup.DefaultContent.Button>
            }
        >
            <Content>
                <Description>
                    {readOnly
                        ? watchersMap.length
                            ? messages.watchersCanOnly
                            : messages.emptyWatchersAndReadonly
                        : messages.description}
                </Description>

                <WatcherList data-qa={qa('watcher-list-block')}>
                    {watchersMap}

                    {possibleWatchers.length > 0 && !readOnly && (
                        <ui.UserPicker
                            qa={qa('user-picker')}
                            selectButtonText={messages.addApproverButtonText}
                            users={possibleWatchers}
                            value={null}
                            onChange={onAddWatcher}
                            theme={ui.UserPicker.Theme.Small}
                        />
                    )}
                </WatcherList>
            </Content>
        </Popup.DefaultContent>
    );
});

export default EditWatchersPopup;
